import { capitalize } from "lodash";
import { useTranslation } from "react-i18next";
import {
  CodeBlock,
  formatDate,
  getSideDrawerContentStyles,
  InfoDrawerMeta,
  SideDrawer,
} from "@inductiveautomation/ws-components";

import { LicenseSession } from "../../Licenses.types";

const SessionsDrawer = ({
  session,
  invalidateSession,
  onClose,
}: {
  session: LicenseSession | undefined;
  invalidateSession: (session: LicenseSession) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { classes: drawerClasses } = getSideDrawerContentStyles();

  return (
    <SideDrawer
      testId="maker-license-session-side-drawer"
      open={!!session}
      onClose={onClose}
      variant="modal"
      hideBackdrop
      contentSpacing="fullWidth"
      headerProps={{
        overline: t("maker_license.tabs.sessions.drawer.overline"),
        heading: session?.name ?? "",
        icon: "session",
        avatarProps: {
          iconAvatarColor: "primary",
        },
        showCloseButton: true,
      }}
      footerProps={{
        actionsLabel: t("common.actions"),
        actions: [
          ...(session?.status === "VALID"
            ? [
                [
                  {
                    id: "invalidate",
                    label: t(
                      "maker_license.tabs.sessions.actions.invalidate.label"
                    ),
                    iconLeft: "license-invalidate" as const,
                    iconLeftVariant: "destructive" as const,
                    onClick: () => invalidateSession(session),
                  },
                ],
              ]
            : [[]]),
        ],
        chip: {
          id: "maker-license-session-status-chip",
          label: capitalize(session?.status.toLowerCase() ?? ""),
          variant: "status",
          state: session?.status === "VALID" ? "info" : "neutral",
        },
      }}
    >
      <InfoDrawerMeta
        heading={t("maker_license.tabs.sessions.drawer.details.heading")}
        fields={[
          {
            label: t("maker_license.tabs.sessions.drawer.details.ip_address"),
            value: session?.ipAddress ?? "",
          },
          {
            label: t("maker_license.tabs.sessions.drawer.details.validated"),
            value: session ? formatDate(session.validated, "date") : "–",
            subtext: session
              ? formatDate(session.validated, "time")
              : undefined,
          },
          ...(session?.invalidated
            ? [
                {
                  label: t(
                    "maker_license.tabs.sessions.drawer.details.invalidated"
                  ),
                  value: formatDate(session.invalidated, "date"),
                  subtext: formatDate(session.invalidated, "time"),
                },
              ]
            : []),
        ]}
      />
      <CodeBlock
        overline={t("maker_license.tabs.sessions.drawer.configuration_heading")}
        language={t("common.code_block.json")}
        className={drawerClasses.contentSectionContainer}
        code={`{
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value,
  “key”: value
}`}
      />
    </SideDrawer>
  );
};

export default SessionsDrawer;
